import { Pic } from "./Pic";

import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Images } from "lucide-react";
import { MaskLines, Reveal, SectionLabel } from "./Motion";
import { ProductGallery } from "./ProductGallery";
import { DragCarousel } from "./DragCarousel";
import { useT } from "@/i18n";
import { scrollToId } from "@/lib/scroll";
import { useJsonLd } from "@/lib/pageMeta";

const askQuote = (type) => {
  window.dispatchEvent(new CustomEvent("prefill-quote", { detail: { project_type: type } }));
  scrollToId(window.innerWidth < 1024 ? "#quote-form" : "#contatti", -110);
};

export const PRODUCTS = [
  { id: "lamina", code: "Legno lamellare BSH", name: "LAMINA", type: "Legno lamellare BSH", desc: "Travi in legno lamellare incollato BSH con marcatura CE (EN 14080), fino a 30 × 280 cm di sezione e 44 m di lunghezza.", img: "/img/emma/p-travi-rette.jpg", span: "lg:w-[560px]" },
  { id: "trama", code: "Pannelli X-LAM", name: "TRAMA", type: "Pannelli X-LAM", desc: "Pannelli a strati incrociati per case in bioedilizia e strutture industriali: i primi prodotti nel Sud Italia.", img: "/img/emma/p-xlam-strati.jpg", span: "lg:w-[440px]" },
  { id: "casa", code: "Case in legno", name: "CASA", type: "Casa in legno", desc: "Case prefabbricate in legno chiavi in mano, con sistema a telaio o X-LAM: progetto, produzione e montaggio da un unico interlocutore, in bioedilizia.", img: "/img/emma/prj-misto-piscina.jpg", span: "lg:w-[560px]" },
  { id: "arco", code: "Lamellare curvo · elementi speciali", name: "ARCO", type: "Lamellare curvo / elementi speciali", desc: "Archi, travi curve ed elementi costruttivi speciali su misura per grandi luci, coperture e architetture complesse.", img: "/img/emma/p-travi-speciali.jpg", span: "lg:w-[440px]" },
  { id: "piano", code: "Elementi per solaio in lamellare", name: "PIANO", type: "Elementi per solaio", desc: "Travi, travetti e orditure in legno lamellare per solai, lavorati su misura e pronti per la posa.", img: "/img/emma/p-solaio.jpg", span: "lg:w-[560px]" },
  { id: "massello", code: "Pannello in legno massiccio", name: "MASSELLO", type: "Pannello legno massiccio", desc: "Pannelli in legno massiccio per pareti, rivestimenti e arredo, con tutto il calore del legno naturale.", img: "/img/emma/p-massello-studio.jpg", span: "lg:w-[440px]" },
  { id: "doga", code: "Perline", name: "DOGA", type: "Perline", desc: "Perline in legno per rivestire pareti, soffitti e sottotetti con finiture calde e naturali.", img: "/img/emma/p-perline-studio.jpg", span: "lg:w-[440px]" },
  { id: "brace", code: "Biomassa · bricchetti", name: "BRACE", type: "Biomassa / Bricchetti", desc: "Biomassa e bricchetti ricavati dagli scarti della nostra lavorazione: energia rinnovabile, senza sprechi.", img: "/img/emma/p-bricchetti-studio.jpg", span: "lg:w-[560px]" },
  { id: "nido", code: "Trucioli depolverati", name: "SCIROCCO", type: "Trucioli depolverati", desc: "Truciolo di pino e abete essiccato e depolverato, senza sostanze tossiche: per stalle, equini, bovini e conigli.", img: "/img/emma/p-trucioli-lettiere.jpg", span: "lg:w-[440px]" },
  { id: "nodo", code: "Carpenteria metallica", name: "NODO", type: "Carpenteria metallica", desc: "Piastre, staffe e connessioni in acciaio progettate e prodotte per le nostre strutture in legno.", img: "/img/emma/p-carpenteria-reale.jpg", span: "lg:w-[440px]" },
];

const Card = ({ p, i, onOpen }) => {
  const t = useT();
  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    e.currentTarget.style.setProperty("--mx", `${e.clientX - r.left}px`);
    e.currentTarget.style.setProperty("--my", `${e.clientY - r.top}px`);
  };
  return (
    <motion.article
      initial={{ opacity: 0, x: 80 }}
      whileInView={{ opacity: 1, x: 0 }}
      viewport={{ once: true, amount: 0.1 }}
      transition={{ duration: 1, delay: Math.min(i, 3) * 0.1, ease: [0.22, 1, 0.36, 1] }}
      onMouseMove={onMove}
      onClick={() => onOpen(p)}
      onKeyDown={(e) => { if (e.key === "Enter" && e.target === e.currentTarget) onOpen(p); }}
      tabIndex={0}
      role="button"
      aria-label={`${t("gal.open", "Apri la galleria")}: ${t(`prod.${p.id}.code`, p.code)}`}
      className={`product-card keep-dark group relative h-[480px] w-[84vw] shrink-0 snap-start overflow-hidden bg-[#121110] text-[#F7F5F0] transition-[border-radius] duration-500 hover:rounded-[28px] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#D98A52] sm:w-[60vw] lg:h-[560px] ${p.span}`}
      data-testid={`product-card-${p.id}`}
    >
      <div className="product-parallax absolute inset-y-0 -left-[8%] w-[116%]">
        <Pic src={p.img} alt={t(`prod.${p.id}.code`, p.code)} draggable={false} className="h-full w-full object-cover opacity-80 transition-[transform,opacity] duration-[1.4s] ease-[cubic-bezier(.22,1,.36,1)] group-hover:scale-[1.06] group-hover:opacity-100" />
      </div>
      <div className="spotlight pointer-events-none absolute inset-0" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#121110]/90 via-[#121110]/30 to-transparent" />
      <div className="absolute inset-x-0 top-0 h-28 bg-gradient-to-b from-[#121110]/70 to-transparent" />
      <div className="relative flex h-full flex-col justify-between p-6 sm:p-8">
        <div className="flex items-start justify-end">
          <span className="font-mono text-xs text-[#F7F5F0]/80">0{i + 1}</span>
        </div>
        <div className="max-w-md transition-transform duration-700 ease-[cubic-bezier(.22,1,.36,1)] group-hover:-translate-y-2">
          <h3 className="font-display text-2xl font-bold leading-tight tracking-tight sm:text-3xl" data-testid={`product-title-${p.id}`}>{t(`prod.${p.id}.code`, p.code)}</h3>
          <div className="mt-5 flex flex-wrap items-center gap-x-6 gap-y-3">
            <button type="button" onClick={(e) => { e.stopPropagation(); askQuote(p.type); }} className="tap inline-flex items-center gap-2 rounded-full bg-[#C26D38] px-5 py-2.5 font-mono text-xs uppercase tracking-[0.2em] text-[#121110] transition-[background-color,transform] duration-300 hover:-translate-y-0.5 hover:bg-[#D98A52]" data-testid={`product-quote-btn-${p.id}`}>
              {t("prod.quote", "Richiedi preventivo")} <ArrowUpRight className="h-4 w-4" />
            </button>
            <span className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-[#F7F5F0]" data-testid={`product-gallery-btn-${p.id}`}>
              <Images className="h-4 w-4" /> {t("gal.cta", "Foto e scheda tecnica")} <ArrowUpRight className="h-4 w-4 transition-transform duration-500 group-hover:rotate-45" />
            </span>
          </div>
        </div>
      </div>
    </motion.article>
  );
};

export const Products = () => {
  const t = useT();
  useJsonLd("products", {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: t("prod.ldName", "Prodotti EMMA INDUSTRIE"),
    itemListElement: PRODUCTS.map((p, i) => ({
      "@type": "ListItem",
      position: i + 1,
      item: {
        "@type": "Product",
        name: t(`prod.${p.id}.code`, p.code),
        description: t(`prod.${p.id}.desc`, p.desc),
        image: `${window.location.origin}${p.img}`,
        url: `${window.location.origin}/?prodotto=${p.id}#prodotti`,
        category: p.type,
        brand: { "@type": "Brand", name: "EMMA INDUSTRIE" },
        manufacturer: { "@type": "Organization", name: "EMMA INDUSTRIE" },
      },
    })),
  });
  const [open, setOpen] = useState(null);
  const { search } = useLocation();
  useEffect(() => {
    const p = PRODUCTS.find((x) => x.id === new URLSearchParams(search).get("prodotto"));
    if (!p) return undefined;
    const timer = setTimeout(() => setOpen(p), 900);
    return () => clearTimeout(timer);
  }, [search]);
  return (
  <section id="prodotti" className="overflow-hidden bg-[#EFECE6] px-5 py-28 text-[#121110] sm:px-8 lg:py-40" data-testid="products-section">
    <div className="mx-auto max-w-[1500px]">
      <div className="grid gap-10 lg:grid-cols-12 lg:items-end">
        <div className="lg:col-span-8">
          <SectionLabel index="03">{t("prod.label", "Prodotti")}</SectionLabel>
          <MaskLines
            className="mt-10 font-display text-4xl font-bold leading-[1.02] tracking-tight sm:text-5xl lg:text-[4.2rem]"
            lines={[t("prod.h1b", "Dieci linee."), <span key="m">{t("prod.h2a", "Una sola")} <em className="font-serif font-normal text-[#C26D38]">{t("prod.h2em", "materia.")}</em></span>]}
          />
        </div>
        <Reveal className="text-base leading-relaxed text-[#6E685E] sm:text-lg lg:col-span-4">
          {t("prod.text", "Ogni elemento nasce nel nostro stabilimento, progettato in BIM e lavorato su centri CNC a 5 assi.")}
        </Reveal>
      </div>
      <DragCarousel id="product" count={PRODUCTS.length} autoplay label={t("prod.carousel", "Prodotti: trascina o usa le frecce")}>
        {PRODUCTS.map((p, i) => <Card key={p.id} p={p} i={i} onOpen={setOpen} />)}
      </DragCarousel>
    </div>
    <ProductGallery product={open} onClose={() => setOpen(null)} onQuote={askQuote} />
  </section>
  );
};
